import { Component, Inject, OnInit, forwardRef, ViewChild, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';

// PrimeNG Modules
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { AvatarModule } from 'primeng/avatar';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { BadgeModule } from 'primeng/badge';
import { TagModule } from 'primeng/tag';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { SelectButtonModule } from 'primeng/selectbutton';
import { LazyLoadEvent, MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';

// Models and Services
import { Incident, IncidentComment } from './incident.model';
import { AppUser } from '../../users/user.model';
import { DividerModule } from 'primeng/divider';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { ResourceService } from '../../master-data/resource/resource.service';
import { Resource } from '../../master-data/resource/resource.model';
import { HttpParams } from '@angular/common/http';
import { IncidentService } from './incident.service';
import { UserService } from '../../users/users.service';
import { AuthenticationService } from '../../services/authentication.service';

@Component({
  selector: 'app-incident-list',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, RouterModule, DividerModule,
    ButtonModule, DialogModule, AvatarModule, TableModule, BadgeModule, TagModule, SelectButtonModule,
    DropdownModule, InputTextModule, TextareaModule, ToastModule, FormsModule,
    InputGroupModule, InputGroupAddonModule, IftaLabelModule
  ],
  providers: [MessageService],
  templateUrl: './incident-list.component.html',
  styleUrls: ['./incident-list.component.css']
})
export class IncidentListComponent implements OnInit {

  @Input() display: 'inline' | 'dialog' = 'dialog';
  @ViewChild('dt') dt: Table | undefined;

  listDialogVisible: boolean = false;
  incidentFormDialogVisible: boolean = false;
  incidentDetailsDialogVisible: boolean = false;

  incidents: Incident[] = [];
  totalRecords: number = 0;
  loading: boolean = false;
  selectedIncident: Incident | null = null;

  incidentForm!: FormGroup;
  commentForm!: FormGroup;

  users: AppUser[] = [];
  statuses: string[] = ['Open', 'In Progress', 'On Hold', 'Resolved', 'Closed'];
  priorities: string[] = ['Critical', 'High', 'Medium', 'Low'];
  resourceList: Resource[] = [];

  currentUser: AppUser | null = null;
  filter: 'all' | 'reportedByMe' | 'assignedToMe' = 'all';
  filterOptions: any[];

  constructor(
    private router: Router,
    private fb: FormBuilder,
    @Inject(forwardRef(() => UserService)) private userService: UserService,
    private resourceService: ResourceService,
    private authService: AuthenticationService,
    private messageService: MessageService,
    private incidentService: IncidentService
  ) {
    this.filterOptions = [
      { label: 'All Incidents', value: 'all' },
      { label: 'Reported By Me', value: 'reportedByMe' },
      { label: 'Assigned To Me', value: 'assignedToMe' }
    ];
  }

  ngOnInit() {
    this.authService.currentUser$.subscribe(user => {
      this.currentUser = user;
    });

    this.incidentForm = this.fb.group({
      title: ['', Validators.required],
      description: [''],
      resource: [null, Validators.required],
      linkedObjectId: [null, [Validators.required, Validators.pattern("^[0-9]*$")]],
      assignedTo: [null],
      priority: ['Medium', Validators.required]
    });

    this.commentForm = this.fb.group({
      commentText: ['', Validators.required]
    });

    this.resourceService.getAllResources(new HttpParams()).subscribe(resources => {
      this.resourceList = resources.content;
    });

    this.userService.getAllUsers().subscribe(users => {
      this.users = users.content;
    });
  }

  onFilterChange() {
    if (this.dt) {
      this.dt.first = 0;
    }
    this.loadIncidents();
  }

  loadIncidents(event?: TableLazyLoadEvent) {
    this.loading = true;
    let params = new HttpParams();

    let page = (event?.first ?? 0) / (event?.rows ?? 25);
    let size = event?.rows ?? 25;
    params = params.set('page', page.toString());
    params = params.set('size', size.toString());

    if (event && event.sortField && event.sortOrder) {
      const sortOrder = event.sortOrder === 1 ? 'asc' : 'desc';
      params = params.set('sort', `${event.sortField},${sortOrder}`);
    }

    if (this.filter === 'reportedByMe' && this.currentUser?.id) {
      params = params.set('reportedBy.id', this.currentUser.id.toString());
    } else if (this.filter === 'assignedToMe' && this.currentUser?.id) {
      params = params.set('assignedTo.id', this.currentUser.id.toString());
    }

    this.incidentService.getAllIncidents(params).subscribe(page => {
      this.incidents = page.content;
      this.totalRecords = page.totalElements;
      this.loading = false;
    });
  }


  showListDialog() { this.listDialogVisible = true; }
  openNewIncidentDialog() { this.incidentFormDialogVisible = true; }

  openIncidentDetails(incident: Incident) {
    this.selectedIncident = { ...incident }; // Work on a copy
    this.incidentDetailsDialogVisible = true;
  }

  saveNewIncident() {
    if (this.incidentForm.invalid) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please fill all required fields.' });
      return;
    }
    const formValue = this.incidentForm.value;
    const newIncidentDto: Partial<Incident> = {
      title: formValue.title,
      description: formValue.description,
      resource: formValue.resource,
      linkedObjectId: formValue.linkedObjectId,
      assignedTo: formValue.assignedTo ? this.users.find(u => u.id === formValue.assignedTo) : undefined,
      priority: formValue.priority,
    };
    newIncidentDto.status = 'Open';
    newIncidentDto.createdAt = new Date();

    this.incidentService.createIncident(newIncidentDto).subscribe({
      next: (createdIncident) => {
        this.incidents.unshift(createdIncident);
        this.incidentFormDialogVisible = false;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Incident created successfully.' });
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to create incident.' });
        console.error(err);
      }
    });
  }

  updateIncident() {
    if (!this.selectedIncident || !this.selectedIncident.incidentId) return;

    this.incidentService.updateIncident(this.selectedIncident.incidentId, this.selectedIncident).subscribe({
      next: (updatedIncident) => {
        const index = this.incidents.findIndex(i => i.incidentId === updatedIncident.incidentId);
        if (index > -1) {
          this.incidents[index] = updatedIncident;
        }
        this.incidentDetailsDialogVisible = false;
        this.selectedIncident = null;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Incident updated.' });
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update incident.' });
        console.error(err);
      }
    });
  }

  addComment() {
    if (this.commentForm.invalid || !this.selectedIncident || !this.selectedIncident.incidentId) return;
    
    const newCommentDto: Partial<IncidentComment> = {
      commentText: this.commentForm.value.commentText,
    };

    this.incidentService.addComment(this.selectedIncident.incidentId, newCommentDto).subscribe({
      next: (createdComment) => {
        if (!this.selectedIncident!.comments) {
          this.selectedIncident!.comments = [];
        }
        this.selectedIncident!.comments.push(createdComment);
        this.commentForm.reset();
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to add comment.' });
        console.error(err);
      }
    });
  }

  getPrioritySeverity(priority: string): string {
    switch (priority) {
      case 'Critical': return 'danger';
      case 'High': return 'danger';
      case 'Medium': return 'warning';
      case 'Low': return 'info';
      default: return 'secondary';
    }
  }

  getStatusSeverity(status: string): string {
    switch (status) {
      case 'Open': return 'info';
      case 'In Progress': return 'warning';
      case 'On Hold': return 'secondary';
      case 'Resolved': return 'success';
      case 'Closed': return 'success';
      default: return 'primary';
    }
  }

  getOpenIncidentCount(): number {
    return this.incidents.filter(i => i.status === 'Open' || i.status === 'In Progress').length;
  }
}
